import { API_BASE_URL } from '@ctcells/core'

import { refreshSession } from './auth/authApi.ts'
import { accessTokenNeedsRefresh, getAccessToken } from './auth/tokenStore.ts'
import { createApiClient, type ApiClient } from './client.ts'
import { markControlLockDenied } from './controlToken.ts'

/**
 * 클라이언트 인스턴스.
 *
 * 🔴 **팩토리는 공유하고 인스턴스는 가른다.** 통합 콘솔(L2)은 Bearer JWT 이고
 *    모듈 로컬(L3)은 계약상 mTLS 또는 무인증이다. 하나로 합치면 **모듈 로컬 요청에 L2 토큰이
 *    새어 나간다** — 망이 분리된 설계 의도를 프론트가 깨는 것이다.
 */

/** 통합 콘솔이 쓰는 L2 게이트웨이 클라이언트 */
export const orchestrator: ApiClient = createApiClient({
  baseUrl: API_BASE_URL,
  getAccessToken,
  refreshAuth: refreshSession,
  // 🔴 만료 «전»에 갱신한다 — 종전에는 화면에 뜬 쿼리가 401 을 한 벌 받고 나서야 돌았다
  //    (2026-09-21 실측 · 다섯이 401 한 벌 뒤 200 한 벌). 판정은 토큰 보관소가 한다.
  shouldRefreshBeforeSend: accessTokenNeedsRefresh,
  // 🔴 409 `CONTROL_LOCK_HELD` 는 **어느 명령에서 나오든** 락 보관소에 반영된다.
  //    명령 훅이 아무것도 안 해도 버튼이 닫히는 이유가 이 한 줄이다.
  onControlLockDenied: markControlLockDenied,
})

/**
 * 모듈 로컬(L3) 클라이언트 팩토리.
 *
 * ⚠ **아직 아무도 부르지 않는다.** 다만 **인증 때문이 아니다.**
 *
 * 🔴 종전 이 자리에 「PIN 로그인 API 가 계약에 없어서 붙일 수 없다」고 적혀 있었다.
 *    **2026-09-20 로컬 실측으로 뒤집혔다 — 되살리지 마라**(`docs/module-local.md` §2).
 *    모듈 로컬은 **무인증**이고(정본 `security: [{mtls:[]}, {}]` 의 `{}` 분기 · 실물
 *    `securitySchemes` 없음), 경로 23개가 정본과 **완전 일치**한다. 무인증으로
 *    `GET /api/v1/status`(`allowed_commands` 12개) · `POST /pause` · `PUT /control-owner`
 *    까지 실제로 돌았다. PIN 은 우리 단말 잠금(`packages/ui/src/shell/padSession.ts`)이지
 *    서버가 검증하는 자격이 아니다 — 없다고 API 가 막히지 않는다.
 *
 * 실제로 막는 것은 셋이고 **그중 둘이 우리 일**이다(`docs/module-local.md` §4):
 *   1. [우리] 모듈 앱 3개의 dev 프록시가 통합 API 한 곳만 가리킨다 — 로컬 API 로 갈 길이 없다
 *      (`packages/build-config/src/vite.ts` — 프록시 3경로가 전부 `apiTarget` 하나)
 *   2. [우리] `mountApp` 이 앱 4개 공용이라 **모듈 앱도 통합 스트림 2채널을 연다.**
 *      패드엔 JWT 가 없어 소켓이 아예 안 열리고 → 영구 stale → 제어 영구 비활성.
 *      🔴 정본 `08 §9`「상위 의존 금지」위반이다
 *   3. [백엔드] 모듈 로컬 구독 2채널(`IFC-WS-05`·`06`)이 **404** — 실물 없음(BR-38).
 *      stale 판정 축이 없어 2번을 고쳐도 제어가 안 열린다
 *
 * 🔸 그래서 `docs/module-local.md` §5-B(갱신 축 — 폴링이냐 조회 전용이냐)가 **eric 결정 대기**다.
 * ⚠ 실장비 «배포»는 별개다 — `08 §9.2` 선결 조건 3개(화면·조작 명세 · 노출 경계 · 귀속 확보)
 *   가 아직 안 섰다(§7). 붙는 것과 붙여도 되는 것은 다른 문제다.
 *
 * 🔴 **Bearer 를 넘기지 않는다.** 이것이 이 팩토리가 따로 있는 이유다.
 */
export function createModuleLocalClient(baseUrl: string): ApiClient {
  return createApiClient({ baseUrl })
}
